import React from 'react';
import styles from './css/index.module.css';

import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, AvatarGroupPopover, AvatarGroupItem, AvatarGroup, Tooltip } from '@fluentui/react-components';
import {
    PersonStarburst20Filled,
    MoreVertical20Regular,
    BuildingBank16Regular,
    HourglassOneQuarter16Regular,
    Target16Regular,
    Person16Regular,
    ErrorCircle16Filled,
    ApprovalsApp16Regular,
    ChevronDown16Regular,
} from '@fluentui/react-icons';
import { useDispatch } from 'react-redux';
import { PatchProjectData } from '../../redux/projectDataSlice';
import { getInitials } from '../../utils/commonUtils';
import { isActiveDuplicateJob } from '../../utils/duplicateProjectJobs';
const ProjectCard = ({
    item,
    user,
    handleEditProject,
    handleArchiveProject,
    openDuplicateConfirm,
    duplicateJobs,
    setTile,
    deleteModalRef,
    handleChangeProject,
    getStatusColor,
    statusOptions,
    calculateColorSM,
    formatLastUpdated,
    convertHtmlToText,
    getTotalFileCount,
    kanban,
}) => {
    const [localStatus, setLocalStatus] = React.useState(item?.status);
    const dispatch = useDispatch();
    React.useEffect(() => {
        setLocalStatus(item?.status);
    }, [item?.status]);

    const handleStatusChange = async (item, newStatus) => {
        setLocalStatus(newStatus);
        await dispatch(PatchProjectData({ data: { status: newStatus }, id: item.id }));
    };
    // Defensive values for IRL chart
    const irlOutstanding = Number(item?.irl_outstanding);
    const irlCompleted = Number(item?.irl_completed);
    // Defensive values for Team chart
    const dev = Number(item?.projectprogress?.development_score);
    const adequate = Number(item?.projectprogress?.adequate_score);
    const exemplary = Number(item?.projectprogress?.exemplary_score);
    // const teamTotal = (!isNaN(dev) ? dev : 0) + (!isNaN(adequate) ? adequate : 0) + (!isNaN(exemplary) ? exemplary : 0);

    // Deadline overdue badge (only show if project_deadline is in the past)
    const isDeadlineOverdue = (() => {
        if (!item?.project_deadline) return false;
        const deadline = new Date(item.project_deadline);
        // Compare with today's date at midnight to avoid time-of-day issues
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return deadline < today;
    })();

    // Task deadline overdue badge (only show if key task deadline is in the past)
    const isTaskDeadlineOverdue = (() => {
        if (!item?.projectprogress?.key_task_info?.deadline) return false;
        const taskDeadline = new Date(item.projectprogress.key_task_info.deadline);
        // Compare with today's date at midnight to avoid time-of-day issues
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return taskDeadline < today;
    })();
    const isProjectAdmin = item?.vdr?.vdr_admin === user?.id || item?.project_admins?.some(admin => admin.user_id === user?.id);

    const isDuplicating = item?.isDuplicatePlaceholder;
    const isSourceDuplicating = duplicateJobs?.some(
        job => job.sourceProjectId === item?.id && isActiveDuplicateJob(job),
    );
    const leadConsultantName =
        item?.team_members?.find(tm => tm?.group_member_id === item?.lead_consultant)?.name || '--';

    return (
        <div key={item?.id} className={`mb-4 position-relative ${isDuplicating ? styles.projectCardDuplicating : ''}`}>
            {isProjectAdmin && !isDuplicating && (
                <div className={styles.menuDiv}>
                    <PersonStarburst20Filled color="#FFC107" />
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <MoreVertical20Regular role="button" color="var(--Neutral-Gray-6)" />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem onClick={() => handleEditProject(item?.id)}>Edit Project</MenuItem>
                                <MenuItem onClick={() => handleArchiveProject(item)}>{item?.archive ? 'Unarchive' : 'Archive'}</MenuItem>
                                <MenuItem
                                    disabled={isSourceDuplicating}
                                    onClick={() => openDuplicateConfirm?.(item)}>
                                    Duplicate Project
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setTile(item);
                                        deleteModalRef?.current?.click();
                                    }}>
                                    Delete Project
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            )}
            <div role="button" onClick={() => !isDuplicating && handleChangeProject(item?.id)} className={styles.projectCard}>
                {isDuplicating && <div className={styles.duplicatingOverlay}>Duplicating project…</div>}
                {/* <div className={styles.workstreamDetails}>
                    <p className={styles.wsTitle}>Workstream</p>
                    <div>
                        {item?.vdr?.workstreams &&
                            Object.keys(item?.vdr?.workstreams)?.map(key => (
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className={styles.wsDetailText}>{key}</div>
                                    <div className={styles.wsDetailText}>{item?.vdr?.workstreams[key]?.files}</div>
                                </div>
                            ))}
                    </div>
                </div> */}
                <div className="">
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <div className={styles.projectType}>{item?.project_type}</div>
                            {!kanban && (
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <div
                                            className={styles.projectStatus}
                                            style={{ backgroundColor: getStatusColor(localStatus), cursor: 'pointer' }}
                                            onClick={e => e.stopPropagation()}>
                                            {localStatus || '-- --'}
                                        </div>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            {statusOptions.map(option => (
                                                <MenuItem
                                                    key={option.value}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleStatusChange(item, option.value);
                                                    }}
                                                    style={{ color: option.color }}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            )}
                        </div>
                        <p className={styles.projectTitle}>{item?.project_name}</p>
                        <div>
                            <div className="d-flex gap-2 align-items-center" style={{ marginBottom: 6 }}>
                                <Target16Regular color="var(--Neutral-Gray-7)" />
                                <p className={styles.companyName}>{item?.company_name || '--'}</p>
                            </div>
                            <div className="d-flex gap-2 align-items-center" style={{ marginBottom: 6 }}>
                                <BuildingBank16Regular color="var(--Neutral-Gray-7)" />
                                <p className={styles.companyName}>{item?.investor_name || '--'}</p>
                            </div>
                            <div className="d-flex gap-2 align-items-center" style={{ marginBottom: 6 }}>
                                <Person16Regular color="var(--Neutral-Gray-7)" />
                                <p className={styles.companyName}>{leadConsultantName}</p>
                            </div>
                            <div className="d-flex gap-2 align-items-center" style={{ marginBottom: 6 }}>
                                <HourglassOneQuarter16Regular color="var(--Neutral-Gray-7)" />
                                <p className={styles.companyName}>
                                    {item?.project_deadline || '--'}{' '}
                                    {isDeadlineOverdue && (
                                        <div
                                            style={{
                                                background: 'white',
                                                display: 'inline-block',
                                                borderRadius: '100%',
                                                width: '9px',
                                                height: '10px',
                                                position: 'relative',
                                                top: '2px',
                                            }}
                                            className="ms-1">
                                            <ErrorCircle16Filled
                                                color="var(--RAG-Red)"
                                                style={{
                                                    position: 'relative',
                                                    top: '-3px',
                                                    left: '-3px',
                                                }}
                                            />
                                        </div>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* <div className={styles.cardSection}>
                            <div className="d-flex align-items-center">
                                <ApprovalsApp16Regular color="var(--Neutral-Gray-7)" />
                                <p className={styles.cardContentLabel + ' ms-2'}>{formatLastUpdated(item?.projectprogress?.last_updated)}</p>
                            </div>
                            <div className="d-flex align-items-center">
                                <DocumentMultiple16Regular color="var(--Neutral-Gray-7)" />
                                <p className={styles.cardContentLabel + ' ms-2'}>
                                    {item?.external_vdr?.vdr_link ? <Globe16Filled color="#00A3EE" /> : getTotalFileCount(item?.vdr?.workstreams)}
                                </p>
                            </div>
                        </div> */}
                        <div className={styles.taskStatus}>
                            <p className={styles.valueLabel}>Task Status</p>
                            <Tooltip
                                withArrow
                                positioning="above"
                                relationship="description"
                                content={
                                    item?.projectprogress?.task_summary && item.projectprogress.task_summary.total > 0 ? (
                                        <div style={{ padding: '4px 0' }}>
                                            <div style={{ marginBottom: '4px' }}>Total Tasks: {item.projectprogress.task_summary.total}</div>
                                            <div style={{ marginBottom: '4px' }}>Completed: {item.projectprogress.task_summary.completed || 0}</div>
                                            <div style={{ marginBottom: '4px' }}>Working on it: {item.projectprogress.task_summary.working_on_it || 0}</div>
                                            <div style={{ marginBottom: '4px' }}>Stuck: {item.projectprogress.task_summary.stuck || 0}</div>
                                            <div style={{ marginBottom: '4px' }}>Not Started: {item.projectprogress.task_summary.not_started || 0}</div>
                                            <div>Cancelled: {item.projectprogress.task_summary.cancelled || 0}</div>
                                        </div>
                                    ) : (
                                        <div>No tasks available</div>
                                    )
                                }>
                                <div style={{ alignSelf: 'stretch', height: 8, justifyContent: 'flex-start', alignItems: 'center', display: 'inline-flex', width: '100%', cursor: 'pointer' }}>
                                    {item?.projectprogress?.task_summary && item.projectprogress.task_summary.total > 0 ? (
                                        <>
                                            {item.projectprogress.task_summary.stuck > 0 && (
                                                <div
                                                    style={{
                                                        width: `${(item.projectprogress.task_summary.stuck / item.projectprogress.task_summary.total) * 100}%`,
                                                        alignSelf: 'stretch',
                                                        background: 'var(--RAG-Red)',
                                                    }}
                                                />
                                            )}
                                            {item.projectprogress.task_summary.working_on_it > 0 && (
                                                <div
                                                    style={{
                                                        width: `${(item.projectprogress.task_summary.working_on_it / item.projectprogress.task_summary.total) * 100}%`,
                                                        alignSelf: 'stretch',
                                                        background: 'var(--RAG-Amber)',
                                                    }}
                                                />
                                            )}
                                            {item.projectprogress.task_summary.completed > 0 && (
                                                <div
                                                    style={{
                                                        width: `${(item.projectprogress.task_summary.completed / item.projectprogress.task_summary.total) * 100}%`,
                                                        alignSelf: 'stretch',
                                                        background: 'var(--RAG-Green)',
                                                    }}
                                                />
                                            )}
                                            {item.projectprogress.task_summary.not_started > 0 && (
                                                <div
                                                    style={{
                                                        width: `${(item.projectprogress.task_summary.not_started / item.projectprogress.task_summary.total) * 100}%`,
                                                        alignSelf: 'stretch',
                                                        background: '#C0C6D8',
                                                    }}
                                                />
                                            )}
                                            {item.projectprogress.task_summary.cancelled > 0 && (
                                                <div
                                                    style={{
                                                        width: `${(item.projectprogress.task_summary.cancelled / item.projectprogress.task_summary.total) * 100}%`,
                                                        alignSelf: 'stretch',
                                                        background: '#989BAD',
                                                    }}
                                                />
                                            )}
                                            {item.projectprogress.task_summary.no_status > 0 && (
                                                <div
                                                    style={{
                                                        width: `${(item.projectprogress.task_summary.no_status / item.projectprogress.task_summary.total) * 100}%`,
                                                        alignSelf: 'stretch',
                                                        background: '#6C757D',
                                                    }}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ flex: '1 1 0', alignSelf: 'stretch', background: '#C0C6D8' }} />
                                    )}
                                </div>
                            </Tooltip>
                        </div>
                        <div>
                            <p className={styles.valueLabel + ' d-flex align-items-center gap-2'}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M7.1939 2.10167C7.52403 1.43275 8.47789 1.43274 8.80802 2.10167L10.3291 5.18372L13.7304 5.67795C14.4685 5.78522 14.7633 6.69239 14.2291 7.21307L11.768 9.61212L12.349 12.9996C12.4751 13.7348 11.7034 14.2955 11.0431 13.9484L8.00096 12.349L4.95879 13.9484C4.29853 14.2955 3.52684 13.7348 3.65294 12.9996L4.23394 9.61212L1.77277 7.21307C1.23861 6.69239 1.53336 5.78522 2.27156 5.67795L5.67281 5.18372L7.1939 2.10167Z"
                                        fill="#FFC107"
                                    />
                                </svg>{' '}
                                Key Task
                            </p>
                            <p className={styles.cardContentLabel + ' ' + styles.keyTaskContainer}>{convertHtmlToText(item?.projectprogress?.key_task)}</p>

                            <div className="d-flex align-items-center gap-2 mt-2">
                                {(() => {
                                    const keyTaskInfo = item?.projectprogress?.key_task_info;
                                    const isAssigned =
                                        keyTaskInfo?.assigned_to_id ||
                                        (keyTaskInfo?.assigned_to && keyTaskInfo.assigned_to !== 'Unassigned');

                                    if (isAssigned) {
                                        return <div className={styles.customAvatar}>{getInitials(keyTaskInfo.assigned_to)}</div>;
                                    }
                                    if (keyTaskInfo) {
                                        return <div className={styles.customAvatarEmpty} aria-hidden="true" />;
                                    }
                                    return null;
                                })()}
                                <p className={styles.companyName}>
                                    {item?.projectprogress?.key_task_info?.deadline || '--'}{' '}
                                    {isTaskDeadlineOverdue && (
                                        <div
                                            style={{
                                                background: 'white',
                                                display: 'inline-block',
                                                borderRadius: '100%',
                                                width: '9px',
                                                height: '10px',
                                                position: 'relative',
                                                top: '2px',
                                            }}
                                            className="ms-1">
                                            <ErrorCircle16Filled
                                                color="var(--RAG-Red)"
                                                style={{
                                                    position: 'relative',
                                                    top: '-3px',
                                                    left: '-3px',
                                                }}
                                            />
                                        </div>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className={styles.footerCard}>
                            <div className="d-flex align-items-center">
                                <ApprovalsApp16Regular color="var(--Neutral-Gray-7)" />
                                <p className={styles.cardContentLabel + ' ms-2'}>{formatLastUpdated(item?.projectprogress?.last_updated)}</p>
                            </div>
                            <Menu positioning="below-end">
                                <MenuTrigger disableButtonEnhancement>
                                    <ChevronDown16Regular color="white" onClick={e => e.stopPropagation()} />
                                </MenuTrigger>
                                <MenuPopover>
                                    <div className={styles.footerCardMenu}>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span>Dataroom Files</span>
                                            <span style={{ color: 'black' }}>{item?.vdr?.documents_count}</span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span>Observations</span>
                                            <div className="d-flex align-items-center">
                                                <div style={{ background: '#CC0000' }} className={styles.rag}>
                                                    {item?.projectprogress?.red_rags || 0}
                                                </div>
                                                <div style={{ background: '#FFC107' }} className={styles.rag}>
                                                    {item?.projectprogress?.amber_rags || 0}
                                                </div>
                                                <div style={{ background: '#198754' }} className={styles.rag}>
                                                    {item?.projectprogress?.green_rags || 0}
                                                </div>
                                            </div>
                                        </div>{' '}
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span>Maturity Index</span>
                                            <div style={{ background: calculateColorSM(parseInt(item?.projectprogress?.maturity_score)) }} className={styles.rag}>
                                                {item?.projectprogress?.maturity_score}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span>Security Index</span>
                                            <div style={{ background: calculateColorSM(parseInt(item?.projectprogress?.security_score)) }} className={styles.rag}>
                                                {item?.projectprogress?.security_score}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span>Security Governance </span>
                                            <div style={{ background: calculateColorSM(parseInt(item?.projectprogress?.governance_score)) }} className={styles.rag}>
                                                {item?.projectprogress?.governance_score}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span>IRL </span>
                                            {(() => {
                                                const outstanding = isNaN(irlOutstanding) ? 0 : irlOutstanding;
                                                const completed = isNaN(irlCompleted) ? 0 : irlCompleted;
                                                const total = outstanding + completed;
                                                if (total <= 0) {
                                                    return (
                                                        <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                                            <div style={{ flex: '1 1 0', background: '#C0C6D8' }} />
                                                        </div>
                                                    );
                                                }
                                                const completedPct = Math.max((completed / total) * 100, 0);
                                                const remainingPct = 100 - completedPct;
                                                return (
                                                    <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                                        {completedPct > 0 && <div style={{ width: `${completedPct}%`, background: '#198754' }} />}
                                                        {remainingPct > 0 && <div style={{ width: `${remainingPct}%`, background: '#C0C6D8' }} />}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span> Team</span>
                                            {(() => {
                                                const d = isNaN(dev) ? 0 : dev;
                                                const a = isNaN(adequate) ? 0 : adequate;
                                                const e = isNaN(exemplary) ? 0 : exemplary;
                                                const total = d + a + e;
                                                if (total <= 0) {
                                                    return (
                                                        <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                                            <div style={{ flex: '1 1 0', background: 'var(--Neutral-Gray-6)' }} />
                                                        </div>
                                                    );
                                                }
                                                const redPct = Math.max((d / total) * 100, 0);
                                                const amberPct = Math.max((a / total) * 100, 0);
                                                const greenPct = Math.max((e / total) * 100, 0);
                                                return (
                                                    <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                                        {redPct > 0 && <div style={{ width: `${redPct}%`, background: 'var(--RAG-Red)' }} />}
                                                        {amberPct > 0 && <div style={{ width: `${amberPct}%`, background: 'var(--RAG-Amber)' }} />}
                                                        {greenPct > 0 && <div style={{ width: `${greenPct}%`, background: 'var(--RAG-Green)' }} />}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div className="d-flex  justify-content-between mb-3">
                                            <span style={{ width: 107, textAlign: 'left', marginTop: 6 }}>Team Members</span>
                                            <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                                                <div className="d-flex align-items-center justify-content-end gap-1 flex-wrap">
                                                    {item?.team_members.map(tm => (
                                                        <Tooltip key={tm?.group_member_id} content={tm?.name} relationship="description" withArrow>
                                                            <div className={styles.teamMember}>{getInitials(tm?.name)}</div>
                                                        </Tooltip>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </MenuPopover>
                            </Menu>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.bottomBorder} style={{ background: getStatusColor(localStatus) }}></div>
            {/* <span className={styles.lastUpdatedText}>Last updated 3 hours ago</span> */}
        </div>
    );
};
export default ProjectCard;
